import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useMemo } from 'react';
import { collect } from 'collect.js';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';
import {
  BusinessType,
  OnboardingStep,
  PaymentGateway,
  PaymentGatewayVerificationStatus,
  PromoCodeTag,
  SubscriptionProductType,
} from '../../services/exports/Constants';
import InfoSubmissionStep from '../../components/verification/InfoSubmissionStep';
import OwnerDocumentStep from '../../components/verification/OwnerDocumentStep';
import BusinessDocumentStep from '../../components/verification/BusinessDocumentStep';
import TaxInfoStep from '../../components/verification/TaxInfoStep';
import BankInfoStep from '../../components/verification/BankInfoStep';
import PersonalInfoStep from '../../components/verification/PersonalInfoStep';
import BusinessInfoStep from '../../components/verification/BusinessInfoStep';
import PaymentInfoStep from '../../components/verification/PaymentInfoStep';
import ContractStep from '../../components/verification/ContractStep';
import ContractPaymentStep from '../../components/verification/ContractPaymentStep';
import { SubscriptionWrapper } from '../../components/verification/SubscriptionStep';
import SubscriptionStep from '../../components/verification/SubscriptionStepStrategy';
import ShippingInfoStep from '../../components/verification/ShippingInfoStep';
import useApiClient from '../api/useApiClient';
import { useGetPromoCodesQuery } from '../../store/api-slices';
import PromoCodeStep from '../../components/verification/PromoCodeStep';
import LoadingScreen from '../../components/loading/LoadingScreen';
import useBusinessProfile from '../context/useBusinessProfile';
import usePermissions, { ManagerRole } from '../auth/usePermissions';
import {
  getRemainingStepsCount,
  getRequiredPersonalFields,
  isOnboardingComplete,
  isStepFinished,
  resolveMerchantBillingForSteps,
  checkBankDetails as ruleCheckBankDetails,
  checkBusinessInfo as ruleCheckBusinessInfo,
  checkDeliveryStructure as ruleCheckDeliveryStructure,
  checkPersonalInfo as ruleCheckPersonalInfo,
  checkShippingInfo as ruleCheckShippingInfo,
  checkTaxDetails as ruleCheckTaxDetails,
  findLastActiveStep as ruleFindLastActiveStep,
  getProgress as ruleGetProgress,
  getRequiredSteps as ruleGetRequiredSteps,
} from './verificationRules';
import MobileAppStep from '../../components/verification/MobileAppStep';
import DeliveryStructureStep from '../../components/verification/DeliveryStructureStep';

export default function useVerification() {
  const { t } = useTranslation(undefined, { keyPrefix: 'Hooks:useVerification' });

  const { MerchantProfileManager, PromoCodesManager } = useApiClient();
  const { business_profile, getValue } = useBusinessProfile();
  const { hasAnyRole } = usePermissions();
  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);
  // A non-billing provider settles with its restaurants outside the platform,
  // so onboarding completes without the Subscription/PaymentInfo/ContractPayment
  // steps (mirrors the backend merchant_billing gates). Fails closed while the
  // flags payload is missing — see resolveMerchantBillingForSteps.
  const hasMerchantBilling = resolveMerchantBillingForSteps(serviceProvider);
  const { company } = useSelector((store) => store.currentCompany);
  const merchant = company?.merchant ?? {};
  const {
    payment_options,
    service_contract,
    has_master_subscription,
    has_mobile_app_subscription,
    onboarding_complete_at,
  } = merchant;

  // Several mounted components use this hook at once (sidebar, onboarding guide, route guard); the
  // shared query sends one request for all of them where a per-instance fetch sent one each.
  const { data: promoCodesPage } = useGetPromoCodesQuery(
    {
      apiManager: PromoCodesManager,
      companyId: company?.id,
      params: {
        filter: {
          active: true,
          tag: PromoCodeTag.Master,
        },
        page: 1,
      },
    },
    {
      skip: !company?.id || hasAnyRole([ManagerRole.StoreEmployee, ManagerRole.StoreOperator]),
      refetchOnMountOrArgChange: true,
    },
  );
  const promoCodes = promoCodesPage?.data ?? [];

  const hasPaymentOption = payment_options?.length > 0;
  const hasMobileAppSubscription =
    has_mobile_app_subscription || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.MobileApp);
  const hasPromoCode =
    promoCodes?.length > 0 || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.PromoCode);
  const hasSignedContract =
    !serviceProvider?.has_merchant_contracts ||
    (service_contract && service_contract.signed_at !== null) ||
    collect(merchant?.skipped_verification_steps).contains(OnboardingStep.Contract);
  // Once the contract has been signed (or the contract step skipped), the
  // merchant is locked out of navigating back through the stepper.
  const isContractLocked =
    Boolean(service_contract?.signed_at) ||
    collect(merchant?.skipped_verification_steps).contains(OnboardingStep.Contract);
  // Immediate contract payment is collected in its own step (before the promo
  // code step). It only applies once there is a contract flagged for immediate
  // payment, and is considered done once that flag clears (i.e. it was paid).
  const requiresContractPayment =
    hasMerchantBilling &&
    serviceProvider?.has_merchant_contracts &&
    Boolean(service_contract?.is_immediate_payment_required);
  const hasPaidContract =
    !requiresContractPayment || collect(merchant?.skipped_verification_steps).contains(OnboardingStep.ContractPayment);

  // No restaurant selected (a reseller browsing provider-wide) means no business profile: every
  // step reads as unfinished and nothing is written back.
  const isCompany = business_profile?.business_type === BusinessType.Company;
  const isIndividual = business_profile?.business_type === BusinessType.Individual;

  const hasStripePayments = useMemo(
    () => (company?.payment_gateways ?? [company?.payment_gateway]).includes(PaymentGateway.Stripe),
    [company?.payment_gateways, company?.payment_gateway],
  );

  const requiredPersonalFields = useMemo(
    () => collect(getRequiredPersonalFields(business_profile?.legal_country)),
    [business_profile?.legal_country],
  );

  const steps = useMemo(
    () => [
      {
        index: 1,
        id: OnboardingStep.BusinessInfo,
        is_required: true,
        is_finished: checkBusinessInfo(),
        name: t(OnboardingStep.BusinessInfo),
        render: (props) => <BusinessInfoStep {...props} />,
      },
      {
        index: 2,
        id: OnboardingStep.PersonalInfo,
        is_required: true,
        is_finished: checkPersonalInfo(),
        name: t(OnboardingStep.PersonalInfo),
        render: (props) => {
          const filtered = {
            ...props,
            moveBackwards: isCompany ? props.moveBackwards : null,
          };

          return <PersonalInfoStep {...filtered} />;
        },
      },
      {
        index: 3,
        id: OnboardingStep.ShippingInfo,
        is_required: true,
        is_finished: checkShippingInfo(),
        name: t(OnboardingStep.ShippingInfo),
        render: (props) => <ShippingInfoStep {...props} />,
      },
      {
        index: 4,
        id: OnboardingStep.BankInfo,
        is_required: true,
        is_finished: checkBankDetails(),
        name: t(OnboardingStep.BankInfo),
        render: (props) => <BankInfoStep {...props} />,
      },
      {
        index: 5,
        id: OnboardingStep.TaxInfo,
        is_required: true,
        is_skippable: true,
        is_finished: checkTaxDetails(),
        name: t(OnboardingStep.TaxInfo),
        render: (props) => <TaxInfoStep isSkippable {...props} />,
      },
      {
        index: 6,
        id: OnboardingStep.Subscription,
        is_required: hasMerchantBilling,
        is_finished: has_master_subscription,
        name: t(OnboardingStep.Subscription),
        render: (props) => (
          <SubscriptionWrapper
            renderSubscriptions={({ loading, subscriptions, subscriptionProducts }) => {
              if (loading) {
                return <LoadingScreen />;
              }

              const masterSubscriptionIndex = collect(subscriptions).search(
                (subscription) =>
                  !!collect(subscription.items).first(
                    (item) =>
                      item.product.type === SubscriptionProductType.Plan ||
                      item.product.type === SubscriptionProductType.LegacyPlan,
                  ),
              );
              const masterSubscription =
                masterSubscriptionIndex !== false ? subscriptions[masterSubscriptionIndex] : null;

              return (
                <SubscriptionStep
                  key="master-subscription"
                  data={masterSubscription}
                  subscriptionProducts={subscriptionProducts}
                  filterOtherProducts={(item) =>
                    item.type !== SubscriptionProductType.GoogleAdsPlan &&
                    item.type !== SubscriptionProductType.MobileAppPlan
                  }
                  {...props}
                />
              );
            }}
          />
        ),
      },
      {
        index: 7,
        id: OnboardingStep.MobileApp,
        is_required: serviceProvider?.is_master,
        is_finished: hasMobileAppSubscription || !!service_contract?.signed_at,
        name: t(OnboardingStep.MobileApp),
        render: (props) => (
          <SubscriptionWrapper
            renderSubscriptions={({ loading, subscriptions, subscriptionProducts }) => {
              if (loading) {
                return <LoadingScreen />;
              }

              const product = collect(subscriptionProducts).firstWhere('type', SubscriptionProductType.MobileAppPlan);
              const mobileAppSubscriptionIndex = collect(subscriptions).search(
                (subscription) =>
                  !!collect(subscription.items).first(
                    (item) => item.product.type === SubscriptionProductType.MobileAppPlan,
                  ),
              );
              const mobileAppSubscription =
                mobileAppSubscriptionIndex !== false ? subscriptions[mobileAppSubscriptionIndex] : null;

              return (
                product && (
                  <MobileAppStep
                    key="mobile-app-subscription"
                    data={mobileAppSubscription}
                    product={product}
                    {...props}
                  />
                )
              );
            }}
          />
        ),
      },
      {
        index: 7.5,
        id: OnboardingStep.DeliveryStructure,
        is_required: serviceProvider?.is_master,
        is_finished: checkDeliveryStructure(),
        name: t(OnboardingStep.DeliveryStructure),
        render: (props) => <DeliveryStructureStep {...props} />,
      },
      {
        index: 8,
        id: OnboardingStep.Contract,
        // Contracts are a billing artifact (setup fees, payment sessions) —
        // gated like the contract nav group and routes.
        is_required: Boolean(serviceProvider?.has_merchant_contracts) && hasMerchantBilling,
        is_skippable: true,
        is_finished: hasSignedContract,
        name: t(OnboardingStep.Contract),
        render: (props) => <ContractStep isSkippable {...props} />,
      },
      {
        index: 9,
        id: OnboardingStep.BusinessDocs,
        is_required: hasStripePayments && isCompany,
        is_finished: merchant.business_document_uploaded,
        name: t(OnboardingStep.BusinessDocs),
        render: (props) => <BusinessDocumentStep {...props} />,
      },
      {
        index: 10,
        id: OnboardingStep.PersonalDocs,
        is_required: hasStripePayments,
        is_finished: merchant.owner_document_uploaded,
        name: t(OnboardingStep.PersonalDocs),
        render: (props) => <OwnerDocumentStep {...props} />,
      },
      ...(company?.payment_gateways ?? []).map((gateway, gatewayIndex) => {
        const status = company?.[`${gateway}_verification_status`];

        return {
          index: 11 + gatewayIndex * 0.1,
          id: `${OnboardingStep.InfoSubmission}_${gateway}`,
          is_required: true,
          is_finished:
            status === PaymentGatewayVerificationStatus.Pending || status === PaymentGatewayVerificationStatus.Verified,
          name: t('confirm_info_gateway', {
            paymentGateway: gateway.charAt(0).toUpperCase() + gateway.slice(1),
          }),
          render: (props) => <InfoSubmissionStep {...props} paymentGateway={gateway} />,
        };
      }),
      {
        index: 12,
        id: OnboardingStep.PaymentInfo,
        is_required: hasMerchantBilling,
        is_finished: hasPaymentOption,
        name: t(OnboardingStep.PaymentInfo),
        render: (props) => <PaymentInfoStep {...props} />,
      },
      {
        index: 12.5,
        id: OnboardingStep.ContractPayment,
        is_required: requiresContractPayment,
        is_finished: hasPaidContract,
        name: t(OnboardingStep.ContractPayment),
        render: (props) => <ContractPaymentStep {...props} />,
      },
      {
        index: 13,
        id: OnboardingStep.PromoCode,
        is_required: true,
        is_skippable: true,
        is_finished: hasPromoCode,
        name: t(OnboardingStep.PromoCode),
        render: (props) => <PromoCodeStep isSkippable {...props} />,
      },
    ],
    [
      business_profile,
      company,
      merchant,
      serviceProvider?.has_merchant_contracts,
      serviceProvider?.is_master,
      hasMerchantBilling,
      hasPaymentOption,
      hasSignedContract,
      requiresContractPayment,
      hasPaidContract,
      service_contract,
      has_master_subscription,
      hasMobileAppSubscription,
      hasPromoCode,
      isCompany,
      hasStripePayments,
    ],
  );

  const isFinished = useCallback(
    (step) => isStepFinished(step, merchant?.skipped_verification_steps),
    [merchant?.skipped_verification_steps],
  );

  const isComplete = useMemo(
    () => isOnboardingComplete(steps, merchant?.skipped_verification_steps, onboarding_complete_at),
    [steps, merchant?.skipped_verification_steps, onboarding_complete_at],
  );

  const markVerificationComplete = async () =>
    MerchantProfileManager.update({
      onboarding_complete_at: moment().format(),
    });

  useEffect(() => {
    isComplete && !onboarding_complete_at && company?.id && markVerificationComplete();
  }, [isComplete]);

  function findLastActiveStep() {
    return ruleFindLastActiveStep(steps, merchant?.skipped_verification_steps);
  }

  function getRequiredSteps() {
    return ruleGetRequiredSteps(steps);
  }

  function getRemainingSteps() {
    return getRemainingStepsCount(steps, merchant?.skipped_verification_steps);
  }

  function getPrevStep(step) {
    return collect(steps).where('index', '<', step.index).where('is_required').last();
  }

  function getNextStep(step) {
    return collect(steps)
      .where('index', '>', step.index)
      .where('is_required')
      .filter((item) => !isFinished(item))
      .first();
  }

  function getStep(index) {
    return collect(steps).firstWhere('index', index);
  }

  function getProgress() {
    return ruleGetProgress(steps, merchant?.skipped_verification_steps);
  }

  function checkBusinessInfo() {
    return ruleCheckBusinessInfo(getValue);
  }

  function checkPersonalInfo() {
    return ruleCheckPersonalInfo(getValue, business_profile?.legal_country);
  }

  function checkShippingInfo() {
    return ruleCheckShippingInfo(getValue);
  }

  function checkBankDetails() {
    return ruleCheckBankDetails(getValue, {
      paymentGateway: company?.payment_gateway,
      stripeBankAccountId: merchant.stripe_bank_account_id,
    });
  }

  function checkTaxDetails() {
    return ruleCheckTaxDetails(getValue);
  }

  function checkDeliveryStructure() {
    return ruleCheckDeliveryStructure(service_contract?.delivery_modes);
  }

  return {
    steps: collect(steps),
    isComplete,
    isContractLocked,
    isCompany,
    isIndividual,
    requiredPersonalFields,
    findLastActiveStep,
    getRequiredSteps,
    getRemainingSteps,
    getPrevStep,
    getNextStep,
    getStep,
    getProgress,
  };
}
